/**
 * IssueRow — one ledger issue (gap or regulatory) rendered as a card row.
 *
 * Shared by the per-section inspector Issues tab (``DataGapsTabPanel``) and the
 * submission-wide Issues tab (``SubmissionIssuesTab``) so both stay visually and
 * behaviourally in sync. Presentational only: the parent owns fetching + the
 * jump / dismiss / reopen / resolve side effects and passes them in as
 * callbacks. Row styling encodes the gap-vs-regulatory + status vocabulary
 * (gap = red #B71C1C, regulatory = blue #1565C0).
 */

import { makeStyles, mergeClasses } from "@fluentui/react-components";
import type React from "react";
import type { SectionIssue } from "../../../api/client";
import { matchFlagMarker } from "../../services/draftSanitizer";
import { fonts, inkColors } from "../../tokens";

/** Sort order for status bands — open first, then dismissed / resolved / stale. */
export const STATUS_ORDER: Record<SectionIssue["status"], number> = {
	open: 0,
	dismissed: 1,
	resolved: 2,
	stale: 3,
};

/** Pretty type label from a marker prefix: "[MISSING INFO:" → "Missing info". */
export function prefixLabel(prefix: string): string {
	const raw = prefix
		.replace(/^\[/, "")
		.replace(/[:\]].*$/, "")
		.trim();
	return raw.charAt(0).toUpperCase() + raw.slice(1).toLowerCase();
}

/** Type label + description for an issue's marker text. */
function issueParts(issue: SectionIssue): { label: string; description: string } {
	const text = issue.marker_text;
	const prefix = matchFlagMarker(text);
	const label = prefix ? prefixLabel(prefix) : prefixLabel(text);
	const colon = text.indexOf(":");
	const description = colon !== -1 ? text.slice(colon + 1, -1).trim() : "";
	return { label, description };
}

/** Writer-facing resolution line, e.g. "Resolved — you edited the document". */
function resolutionLabel(issue: SectionIssue): string | null {
	if (issue.status === "open") return null;
	if (issue.status === "stale") return "Superseded — section was regenerated";
	if (issue.status === "dismissed") {
		// Chip already reads "Done"; only add a line when there's a note.
		const note = issue.resolution?.note;
		return note ? `Done — ${note}` : null;
	}
	const method = issue.resolution?.method;
	if (method === "agent_edit") return "Resolved — asthra edit";
	if (method === "writer_manual") return "Resolved — writer edit";
	return "Resolved";
}

/** Chip text for an issue status — dismissed reads as "Done" for writers. */
function statusChipLabel(status: SectionIssue["status"]): string {
	return status === "dismissed" ? "Done" : status;
}

/** Rank a regulatory finding's severity for sorting (critical first). */
export function severityRank(severity?: string | null): number {
	switch ((severity || "").toLowerCase()) {
		case "critical":
			return 0;
		case "major":
			return 1;
		case "minor":
			return 2;
		default:
			return 3;
	}
}

/** Chip label + colour for a severity, or null when it isn't a real severity
 *  (e.g. the legacy "info" that predates per-finding severity). */
function severityMeta(severity?: string | null): { label: string; bg: string } | null {
	switch ((severity || "").toLowerCase()) {
		case "critical":
			return { label: "Critical", bg: "#B71C1C" };
		case "major":
			return { label: "Major", bg: "#C77700" };
		case "minor":
			return { label: "Minor", bg: "#5A6B7B" };
		default:
			return null;
	}
}

/** Human tag for a regulatory finding's category, or null. */
function categoryLabel(category?: string | null): string | null {
	switch ((category || "").toLowerCase()) {
		case "precedent_signal":
		case "precedent_note":
			return "Precedent";
		case "source_consistency":
			return "Consistency";
		case "guidance_expectation":
			return "Guidance";
		default:
			return null;
	}
}

const useStyles = makeStyles({
	row: {
		display: "block",
		width: "100%",
		textAlign: "left",
		borderRadius: "5px",
		padding: "8px 10px",
		fontFamily: fonts.body,
		border: "none",
	},
	rowOpen: {
		background: "#FFF4F4",
		borderLeft: "3px solid #B71C1C",
	},
	rowOpenRegulatory: {
		background: "#F0F6FF",
		borderLeft: "3px solid #1565C0",
	},
	rowOpenLanguage: {
		background: "#EEF7F0",
		borderLeft: "3px solid #1B5E20",
	},
	rowResolved: {
		background: "#F2FAF3",
		borderLeft: "3px solid #2E7D32",
		opacity: 0.85,
	},
	rowDismissed: {
		background: "#F5F5F5",
		borderLeft: "3px solid #9E9E9E",
		opacity: 0.85,
	},
	rowStale: {
		background: "#FFF9EC",
		borderLeft: "3px solid #B08800",
		opacity: 0.85,
	},
	rowTop: {
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		gap: "6px",
		marginBottom: "3px",
	},
	rowLabelBtn: {
		border: "none",
		background: "transparent",
		padding: 0,
		cursor: "pointer",
		textAlign: "left",
		flex: "1 1 auto",
		":focus-visible": { outline: "2px solid #B71C1C", outlineOffset: "1px" },
	},
	rowLabel: {
		fontSize: "9px",
		fontWeight: 700,
		letterSpacing: "0.04em",
		textTransform: "uppercase",
		color: "#B71C1C",
	},
	rowLabelRegulatory: { color: "#1565C0" },
	rowLabelLanguage: { color: "#1B5E20" },
	chip: {
		fontSize: "8.5px",
		fontWeight: 700,
		letterSpacing: "0.04em",
		textTransform: "uppercase",
		padding: "1px 6px",
		borderRadius: "8px",
		whiteSpace: "nowrap",
		flex: "0 0 auto",
	},
	chipOpen: { background: "#B71C1C", color: "#FFFFFF" },
	chipResolved: { background: "#2E7D32", color: "#FFFFFF" },
	chipDismissed: { background: "#9E9E9E", color: "#FFFFFF" },
	chipStale: { background: "#B08800", color: "#FFFFFF" },
	rowDesc: {
		fontSize: "11.5px",
		color: inkColors.ink2,
		lineHeight: 1.4,
		wordBreak: "break-word",
	},
	resolutionLine: {
		fontSize: "10px",
		color: inkColors.ink4,
		marginTop: "3px",
		fontStyle: "italic",
	},
	findingIssue: {
		fontSize: "11px",
		color: inkColors.ink3,
		lineHeight: 1.4,
		marginTop: "4px",
		wordBreak: "break-word",
	},
	toResolve: {
		fontSize: "11px",
		color: inkColors.ink2,
		lineHeight: 1.4,
		marginTop: "4px",
		wordBreak: "break-word",
	},
	toResolveLabel: {
		fontSize: "8.5px",
		fontWeight: 700,
		letterSpacing: "0.04em",
		textTransform: "uppercase",
		color: "#1565C0",
		marginRight: "5px",
	},
	// Severity + category meta row for regulatory findings (under the label).
	rowMeta: {
		display: "flex",
		alignItems: "center",
		gap: "5px",
		marginBottom: "3px",
		flexWrap: "wrap",
	},
	sevChip: {
		fontSize: "8.5px",
		fontWeight: 700,
		letterSpacing: "0.04em",
		textTransform: "uppercase",
		padding: "1px 6px",
		borderRadius: "8px",
		whiteSpace: "nowrap",
		color: "#FFFFFF",
	},
	catTag: {
		fontSize: "8.5px",
		fontWeight: 700,
		letterSpacing: "0.04em",
		textTransform: "uppercase",
		padding: "1px 6px",
		borderRadius: "8px",
		whiteSpace: "nowrap",
		color: "#1565C0",
		border: "1px solid #9EC1EE",
		background: "#EAF2FD",
	},
	// Verified evidence block: quote + source line, one per citation.
	evidenceBlock: {
		marginTop: "5px",
		paddingLeft: "7px",
		borderLeft: "2px solid #9EC1EE",
		display: "flex",
		flexDirection: "column",
		gap: "4px",
	},
	evidenceLabel: {
		fontSize: "8.5px",
		fontWeight: 700,
		letterSpacing: "0.04em",
		textTransform: "uppercase",
		color: "#1565C0",
	},
	evidenceQuote: {
		fontSize: "10.5px",
		color: inkColors.ink2,
		lineHeight: 1.4,
		fontStyle: "italic",
		wordBreak: "break-word",
	},
	evidenceSource: {
		fontSize: "9.5px",
		color: inkColors.ink4,
		wordBreak: "break-word",
	},
	// Selection checkbox (bulk-resolve). Sits at the row's top-left, before the
	// type label. Only rendered for open issues via ``selectable``.
	selectRow: {
		display: "flex",
		alignItems: "flex-start",
		gap: "8px",
	},
	checkbox: {
		marginTop: "2px",
		flex: "0 0 auto",
		cursor: "pointer",
		accentColor: "#1565C0",
	},
	rowBody: { flex: "1 1 auto", minWidth: 0 },
	actions: {
		display: "flex",
		gap: "8px",
		marginTop: "6px",
	},
	actionBtn: {
		border: "1px solid #D9D9D9",
		background: "#FFFFFF",
		borderRadius: "4px",
		fontFamily: fonts.body,
		fontSize: "10px",
		color: inkColors.ink2,
		padding: "2px 8px",
		cursor: "pointer",
		":hover": { background: "#F5F5F5" },
	},
});

export interface IssueRowProps {
	issue: SectionIssue;
	/** Select this marker in the live Word document (open rows only). */
	onJump: (issue: SectionIssue) => void;
	/** Dismiss ("Mark N/A") — no document edit required. */
	onDismiss: (issue: SectionIssue) => void;
	/** Reopen a dismissed issue. */
	onReopen: (issue: SectionIssue) => void;
	/** Optional host-wired "resolve with asthra". Button hidden when absent. */
	onResolveWithAsthra?: (issue: SectionIssue) => void;
	/** Bulk-resolve selection: when true (open issues only), render a checkbox. */
	selectable?: boolean;
	/** Whether this row is currently selected. */
	selected?: boolean;
	/** Toggle selection for this issue. */
	onToggleSelect?: (issue: SectionIssue) => void;
}

/** One ledger row: type label + status chip, marker body, regulatory guidance,
 *  resolution line, and per-status actions. */
export const IssueRow: React.FC<IssueRowProps> = ({
	issue,
	onJump,
	onDismiss,
	onReopen,
	onResolveWithAsthra,
	selectable,
	selected,
	onToggleSelect,
}) => {
	const styles = useStyles();
	const { label, description } = issueParts(issue);
	const isRegulatory = issue.kind === "regulatory";
	const isLanguage = issue.kind === "language";
	const isOpen = issue.status === "open";
	const sevMeta = isRegulatory ? severityMeta(issue.severity) : null;
	const catTag = isRegulatory ? categoryLabel(issue.category) : null;
	const evidence = isRegulatory ? issue.finding_evidence ?? [] : [];
	const rowStatusClass = isOpen
		? isRegulatory
			? styles.rowOpenRegulatory
			: isLanguage
				? styles.rowOpenLanguage
				: styles.rowOpen
		: issue.status === "resolved"
			? styles.rowResolved
			: issue.status === "dismissed"
				? styles.rowDismissed
				: styles.rowStale;
	const chipClass = isOpen
		? styles.chipOpen
		: issue.status === "resolved"
			? styles.chipResolved
			: issue.status === "dismissed"
				? styles.chipDismissed
				: styles.chipStale;
	const resLabel = resolutionLabel(issue);
	const showCheckbox = Boolean(selectable && isOpen);

	const body = (
		<>
			<div className={styles.rowTop}>
				<button
					type="button"
					className={styles.rowLabelBtn}
					onClick={() => isOpen && onJump(issue)}
					title={isOpen ? "Select this marker in the Word document" : undefined}
				>
					<span
						className={mergeClasses(
							styles.rowLabel,
							isRegulatory ? styles.rowLabelRegulatory : isLanguage ? styles.rowLabelLanguage : undefined,
						)}
					>
						{label}
					</span>
				</button>
				<span className={mergeClasses(styles.chip, chipClass)}>{statusChipLabel(issue.status)}</span>
			</div>
			{isRegulatory && (sevMeta || catTag) ? (
				<div className={styles.rowMeta}>
					{sevMeta ? (
						<span className={styles.sevChip} style={{ background: sevMeta.bg }}>
							{sevMeta.label}
						</span>
					) : null}
					{catTag ? <span className={styles.catTag}>{catTag}</span> : null}
				</div>
			) : null}
			<div className={styles.rowDesc}>{description || issue.marker_text}</div>
			{isRegulatory && issue.finding_issue ? <div className={styles.findingIssue}>{issue.finding_issue}</div> : null}
			{isRegulatory && issue.finding_resolution ? (
				<div className={styles.toResolve}>
					<span className={styles.toResolveLabel}>To resolve</span>
					{issue.finding_resolution}
				</div>
			) : null}
			{isRegulatory && evidence.length > 0 ? (
				<div className={styles.evidenceBlock}>
					<span className={styles.evidenceLabel}>Evidence</span>
					{evidence.map((ev, i) => {
						const src = [ev.authority, ev.source_title, ev.locator]
							.map((s) => (s || "").trim())
							.filter(Boolean)
							.join(" — ");
						return (
							<div key={i}>
								{ev.quote ? <div className={styles.evidenceQuote}>“{ev.quote}”</div> : null}
								{src ? <div className={styles.evidenceSource}>{src}</div> : null}
							</div>
						);
					})}
				</div>
			) : null}
			{resLabel ? <div className={styles.resolutionLine}>{resLabel}</div> : null}
			{isOpen ? (
				<div className={styles.actions}>
					{onResolveWithAsthra ? (
						<button
							type="button"
							className={styles.actionBtn}
							onClick={() => onResolveWithAsthra(issue)}
							title="Ask asthra to research and resolve this marker"
						>
							Resolve with asthra
						</button>
					) : null}
					<button
						type="button"
						className={styles.actionBtn}
						onClick={() => onDismiss(issue)}
						title="Mark this gap as not applicable — no document edit needed"
					>
						Mark N/A
					</button>
				</div>
			) : issue.status === "dismissed" ? (
				<div className={styles.actions}>
					<button type="button" className={styles.actionBtn} onClick={() => onReopen(issue)}>
						Reopen
					</button>
				</div>
			) : null}
		</>
	);

	return (
		<div className={mergeClasses(styles.row, rowStatusClass)}>
			{showCheckbox ? (
				<div className={styles.selectRow}>
					<input
						type="checkbox"
						className={styles.checkbox}
						checked={Boolean(selected)}
						onChange={() => onToggleSelect?.(issue)}
						aria-label="Select issue to resolve with asthra"
					/>
					<div className={styles.rowBody}>{body}</div>
				</div>
			) : (
				body
			)}
		</div>
	);
};

export default IssueRow;
